/**
 * Advanced Image Optimization
 * Handles lazy loading, WebP detection, and performance optimization
 */

class ImageOptimization {
  constructor() {
    this.supportsWebP = false;
    this.supportsAvif = false;
    this.observer = null;
    
    this.init();
  }

  async init() {
    // Detect modern image format support
    await this.detectImageFormats();
    
    // Initialize intersection observer for lazy loading
    this.initLazyLoading();
    
    // Optimize existing images
    this.optimizeExistingImages();
  }

  async detectImageFormats() {
    // Test WebP support
    this.supportsWebP = await this.testImageFormat('data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA');
    
    // Test AVIF support
    this.supportsAvif = await this.testImageFormat('data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgABogQEAwgMg8f8D///8WfhwB8+ErK42A=');
    
    console.log('Image format support:', { webp: this.supportsWebP, avif: this.supportsAvif });
  }

  testImageFormat(dataUrl) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = dataUrl;
    });
  }

  initLazyLoading() {
    if (!('IntersectionObserver' in window)) {
      // Fallback for older browsers
      this.loadAllImages();
      return;
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadImage(entry.target);
          this.observer.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '50px 0px', // Start loading 50px before image enters viewport
      threshold: 0.01
    });

    // Observe all lazy images
    document.querySelectorAll('img[loading="lazy"]').forEach(img => {
      this.observer.observe(img);
    });
  }

  loadImage(img) {
    // If image has data-src, swap it with src
    if (img.dataset.src) {
      img.src = img.dataset.src;
      delete img.dataset.src;
    }

    // If image has data-srcset, swap it with srcset
    if (img.dataset.srcset) {
      img.srcset = img.dataset.srcset;
      delete img.dataset.srcset;
    }

    // Add loaded class for styling
    img.addEventListener('load', () => {
      img.classList.add('loaded');
    });
  }

  loadAllImages() {
    // Fallback: load all images immediately
    document.querySelectorAll('img[data-src]').forEach(img => {
      this.loadImage(img);
    });
  }

  optimizeExistingImages() {
    // Add fade-in effect for images
    document.querySelectorAll('img').forEach(img => {
      if (img.complete && img.naturalHeight !== 0) {
        img.style.opacity = '1';
      } else {
        img.addEventListener('load', () => {
          img.style.opacity = '1';
        });
      }
    });
  }

  // Utility method to create optimized image URLs
  getOptimizedImageUrl(baseUrl, options = {}) {
    const {
      width = 600,
      height = null,
      format = 'auto',
      quality = 85
    } = options;

    let url = baseUrl;
    
    // Add width parameter
    url += url.includes('?') ? '&' : '?';
    url += `width=${width}`;
    
    // Add height if specified
    if (height) {
      url += `&height=${height}`;
    }
    
    // Add format if specified and supported
    if (format === 'auto') {
      if (this.supportsAvif) {
        url += '&format=avif';
      } else if (this.supportsWebP) {
        url += '&format=webp';
      }
    } else if (format !== 'original') {
      url += `&format=${format}`;
    }
    
    // Add quality
    url += `&quality=${quality}`;
    
    return url;
  }
}

// Initialize image optimization when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.imageOptimization = new ImageOptimization();
});

// Export for use in other scripts
window.ImageOptimization = ImageOptimization;
